import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { cn } from "../../lib/utils.js";
import {
  Activity,
  Copy,
  Link2,
  Lock,
  Mail,
  Send,
  Share2,
  UserPlus,
  Users,
  Video,
  Phone,
  Clock,
} from "lucide-react";

const CALL_TYPES = [
  { key: "video", label: "Video", icon: Video },
  { key: "voice", label: "Voice", icon: Phone },
];

function formatLogTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function CallDetailsPanel({
  callType,
  onCallTypeChange,
  callLink,
  onCopyLink,
  onShareLink,
  onSendLink,
  onAddPeople,
  encryptionLabel = "End-to-end encrypted",
  recentDial,
  upcomingMeeting,
  directory = [],
  activeParticipants = [],
  activityLog = [],
  historyEntries = [],
  dialHistory = [],
}) {
  return (
    <div className="glass-card flex h-full flex-col justify-between rounded-3xl p-6 text-sm">
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
            <span>Call controls</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-400">
              <Lock className="h-3 w-3" /> {encryptionLabel}
            </span>
          </div>
          <div className="mt-3 inline-flex gap-2 rounded-full bg-muted/30 p-1">
            {CALL_TYPES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => onCallTypeChange?.(key)}
                className={cn(
                  "flex items-center gap-1 rounded-full px-3 py-2 text-xs font-semibold transition",
                  callType === key ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"
                )}
                aria-pressed={callType === key}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Join link</label>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={callLink} className="flex-1 text-xs" />
            <Button type="button" variant="outline" size="sm" onClick={onCopyLink}>
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Button type="button" size="sm" variant="secondary" onClick={onSendLink}>
              <Send className="h-3.5 w-3.5" /> Send link
            </Button>
            <Button type="button" size="sm" variant="ghost" className="border border-border bg-background/60" onClick={onShareLink}>
              <Share2 className="h-3.5 w-3.5" /> Share
            </Button>
            <Button type="button" size="sm" variant="ghost" className="border border-border bg-background/60" onClick={onAddPeople}>
              <Users className="h-3.5 w-3.5" /> Add people
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Active participants
          </div>
          {activeParticipants.length ? (
            <ul className="mt-3 space-y-2">
              {activeParticipants.map((person) => {
                const key = person.id || person.email || person.name;
                return (
                  <li key={key} className="flex items-center justify-between rounded-xl border border-transparent bg-background/50 px-3 py-2 text-xs">
                    <span className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(person.email || person.name)}`} alt={person.name} referrerPolicy="no-referrer" />
                        <AvatarFallback>{person.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span>
                        <span className="block text-sm font-semibold text-foreground">{person.name}</span>
                        {person.email && <span className="text-[11px] text-muted-foreground">{person.email}</span>}
                      </span>
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{person.role || "Participant"}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="mt-3 text-xs text-muted-foreground">No participants added yet.</div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <UserPlus className="h-3.5 w-3.5" /> Frequently invited
          </div>
          {directory.length ? (
            <div className="mt-3 grid gap-2">
              {directory.map((user) => (
                <button
                  key={user.id || user.email || user.name}
                  type="button"
                  onClick={() => onAddPeople?.(user)}
                  className="flex items-center justify-between rounded-xl border border-transparent bg-background/50 px-3 py-2 text-left text-xs transition hover:border-primary/40 hover:bg-primary/5"
                >
                  <span className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(user.email || user.name)}`} alt={user.name} referrerPolicy="no-referrer" />
                      <AvatarFallback>{user.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span>
                      <span className="block text-sm font-semibold text-foreground">{user.name}</span>
                      {user.email && <span className="text-[11px] text-muted-foreground">{user.email}</span>}
                    </span>
                  </span>
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-xs text-muted-foreground">Connect an address book to see suggestions.</div>
          )}
        </div>

        <div className="grid gap-3 text-xs">
          <div className="rounded-2xl border border-border bg-muted/40 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> Upcoming
            </div>
            {upcomingMeeting ? (
              <div className="mt-3 space-y-1 text-sm">
                <div className="font-semibold text-foreground">{upcomingMeeting.title}</div>
                <div className="text-muted-foreground">{upcomingMeeting.window}</div>
                <div className="text-muted-foreground">Type: {upcomingMeeting.callType}</div>
              </div>
            ) : (
              <div className="mt-3 text-muted-foreground">No scheduled calls yet.</div>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-muted/40 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Link2 className="h-3.5 w-3.5" /> Recent bridge
            </div>
            {recentDial ? (
              <div className="mt-3 space-y-1 text-sm">
                <div className="font-semibold text-foreground">{recentDial.callerId}</div>
                <div className="text-muted-foreground">Dialed {recentDial.phone}</div>
                {recentDial.notes && <div className="text-muted-foreground">Note: {recentDial.notes}</div>}
                {recentDial.ts && <div className="text-xs text-muted-foreground">{new Date(recentDial.ts).toLocaleString()}</div>}
              </div>
            ) : (
              <div className="mt-3 text-muted-foreground">No outbound activity yet today.</div>
            )}
            {dialHistory.length > 1 && (
              <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                {dialHistory.slice(0, 5).map((entry, index) => (
                  <li key={entry.id || `dial-${index}`} className="flex items-center justify-between rounded-xl bg-background/40 px-3 py-2">
                    <span>
                      <span className="block font-semibold text-foreground">{entry.callerId || "Outbound"}</span>
                      <span className="text-xs">{entry.phone}</span>
                      {entry.notes && <span className="block text-[11px] text-muted-foreground">{entry.notes}</span>}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{formatLogTime(entry.ts)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {historyEntries.length > 0 && (
            <div className="rounded-2xl border border-border bg-muted/40 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Activity className="h-3.5 w-3.5" /> Saved history
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {historyEntries.slice(0, 8).map((entry) => (
                  <li key={entry.id} className="flex items-start justify-between gap-3 rounded-xl bg-background/40 px-3 py-2">
                    <span>
                      <span className="block font-semibold text-foreground">{entry.title}</span>
                      <span className="text-xs text-muted-foreground">{entry.detail}</span>
                    </span>
                    <span className="text-[11px] text-muted-foreground">{formatLogTime(entry.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-muted/40 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Activity className="h-3.5 w-3.5" /> Activity log
            </div>
            {activityLog.length ? (
              <ul className="mt-3 space-y-2 text-sm">
                {activityLog.map((entry) => (
                  <li key={entry.id} className="flex items-start justify-between gap-3 rounded-xl bg-background/40 px-3 py-2">
                    <span>
                      <span className="block font-semibold text-foreground">{entry.title}</span>
                      <span className="text-xs text-muted-foreground">{entry.detail}</span>
                    </span>
                    <span className="text-[11px] text-muted-foreground">{formatLogTime(entry.at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-3 text-muted-foreground">Share, send, or invite to track engagement.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
